import React, { useEffect, useState } from 'react';
import { initPixel, trackPageView, trackAddToCart } from './utils/metaPixel';
import { CheckIcon, XIcon, ShieldCheckIcon } from 'lucide-react';
import { ExpandableSection } from './components/ExpandableSection';
import { FAQItem } from './components/FAQItem';
import { Footer } from './components/Footer';
import { BonusSection } from './components/BonusSection';
import { ValueStack } from './components/ValueStack';
export function App() {
  useEffect(() => {
    initPixel('1154323073507518');
    trackPageView();
  }, []);
  const handleAddToCart = () => {
    trackAddToCart({
      content_name: 'Mascota Sola: Guía Anti-Destrucción',
      content_type: 'product',
      value: 8499,
      currency: 'ARS'
    });
    window.open('https://amigospeludosarg.shop/cart/49062143131890:1', '_blank');
  };
  const scrollToCTA = () => {
    const ctaSection = document.getElementById('cta-section');
    ctaSection?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const [showBonus1, setShowBonus1] = useState(false);
  const [showBonus2, setShowBonus2] = useState(false);
  const [showBonus3, setShowBonus3] = useState(false);
  return <div className="w-full min-h-screen bg-cream-50" data-id="element-0">
      {/* Hero Section */}
      <section className="bg-white py-8 md:py-20" data-id="element-1">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center" data-id="element-3">
            <div data-id="element-4">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-navy-900 leading-tight mb-4 md:mb-6" data-id="element-5">
                El Método de los 7 Minutos: Detené los destrozos y los aullidos
                de tu perro hoy mismo
              </h1>
              <h2 className="text-lg md:text-2xl text-gray-700 mb-6 md:mb-8 leading-relaxed" data-id="element-6">
                La guía definitiva para que tu mascota descanse mientras
                trabajás, sin gastar una fortuna en adiestradores ni medicación.
              </h2>
              <button onClick={scrollToCTA} className="bg-orange-600 hover:bg-orange-700 text-white font-bold py-3 md:py-4 px-6 md:px-8 rounded-lg text-base md:text-lg transition-all transform hover:scale-105 shadow-lg" data-id="element-7">
                Quiero el Método de los 7 Minutos ahora
              </button>
            </div>
            <div className="flex justify-center" data-id="element-8">
              <img src="https://cdn.magicpatterns.com/uploads/gnbtUV32q9HPhUMH9igADJ/Mockup.png" alt="Mascota Sola - Guía Anti-Destrucción" className="w-full max-w-xs md:max-w-md rounded-lg shadow-2xl" data-id="element-9" />
            </div>
          </div>
        </div>
      </section>

      {/* Opening Hook */}
      <section className="bg-cream-50 py-10 md:py-16" data-id="element-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-11">
          <p className="text-lg md:text-xl text-gray-800 leading-relaxed mb-4 md:mb-6 italic" data-id="element-12">
            Ponés la llave en la cerradura y contenés el aliento. En ese segundo
            de silencio, rezás para que hoy no haya pedazos de colchón en el
            pasillo o una nota de queja de tus vecinos en la puerta.
          </p>
          <p className="text-lg md:text-xl text-gray-800 leading-relaxed font-medium" data-id="element-13">
            Si amás a tu perro pero sentís que tu casa es una zona de guerra y
            tu libertad está condicionada por sus crisis de ansiedad, este
            mensaje es para vos.
          </p>
        </div>
      </section>

      {/* The Problem */}
      <section className="bg-white py-10 md:py-16" data-id="element-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-15">
          <h2 className="text-2xl md:text-4xl font-bold text-navy-900 mb-5 md:mb-8" data-id="element-16">
            El Problema Real
          </h2>
          <ExpandableSection preview={<p className="text-lg text-gray-700 leading-relaxed" data-id="element-18">
                No es capricho y no es venganza. Tu perro no rompe el sillón
                porque "está enojado" contigo. Lo hace porque el pánico de
                quedarse solo eleva su cortisol a niveles insoportables, y
                masticar tus zapatillas es la única forma biológica que conoce
                para no colapsar.
              </p>} fullContent={<p className="text-lg text-gray-700 leading-relaxed mt-4" data-id="element-19">
                Vivir con un perro con ansiedad por separación en un
                departamento de Argentina es agotador. El miedo al desalojo por
                los ruidos molestos y la culpa de dejarlo sufriendo te están
                quitando la paz mental que merecés al terminar tu jornada
                laboral.
              </p>} data-id="element-17" />
        </div>
      </section>

      {/* Why What You Tried Didn't Work */}
      <section className="bg-cream-50 py-10 md:py-16" data-id="element-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-21">
          <h2 className="text-2xl md:text-4xl font-bold text-navy-900 mb-5 md:mb-8" data-id="element-22">
            Por Qué Lo Que Intentaste No Funcionó
          </h2>
          <ExpandableSection preview={<div className="space-y-4" data-id="element-24">
                <div className="flex gap-4" data-id="element-25">
                  <XIcon className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" data-id="element-26" />
                  <p className="text-lg text-gray-700 leading-relaxed" data-id="element-27">
                    <strong data-id="element-28">
                      Despedirte con besos y promesas de "ya vuelvo"
                    </strong>{' '}
                    solo le inyecta adrenalina justo antes de la soledad.
                  </p>
                </div>
                <div className="flex gap-4" data-id="element-29">
                  <XIcon className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" data-id="element-30" />
                  <p className="text-lg text-gray-700 leading-relaxed" data-id="element-31">
                    <strong data-id="element-32">
                      Retarlo cuando llegás y encontrás el desastre
                    </strong>{' '}
                    solo le enseña que tu regreso es peligroso, aumentando su
                    ansiedad para la próxima vez.
                  </p>
                </div>
              </div>} fullContent={<div className="space-y-4 mt-4" data-id="element-33">
                <div className="flex gap-4" data-id="element-34">
                  <XIcon className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" data-id="element-35" />
                  <p className="text-lg text-gray-700 leading-relaxed" data-id="element-36">
                    <strong data-id="element-37">Dejar la televisión prendida</strong> se convierte
                    en una señal de que te vas, disparando su pánico apenas
                    escucha el sonido del informativo.
                  </p>
                </div>
                <div className="flex gap-4" data-id="element-38">
                  <XIcon className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" data-id="element-39" />
                  <p className="text-lg text-gray-700 leading-relaxed" data-id="element-40">
                    <strong data-id="element-41">Los juguetes caros no sirven</strong> si su cerebro
                    está en modo supervivencia; un perro aterrado no juega,
                    sobrevive.
                  </p>
                </div>
              </div>} data-id="element-23" />
        </div>
      </section>

      {/* The Unique Mechanism */}
      <section className="bg-white py-10 md:py-16" data-id="element-42">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-43">
          <h2 className="text-2xl md:text-4xl font-bold text-navy-900 mb-4 md:mb-6" data-id="element-44">
            El Método de los <span className="text-orange-600" data-id="element-45">7 Minutos</span>{' '}
            de Calma Inicial
          </h2>
          <p className="text-base md:text-lg text-gray-700 leading-relaxed" data-id="element-46">
            Este sistema ignora las teorías largas y se enfoca en la{' '}
            <strong data-id="element-47">"Gestión de Umbrales"</strong>. Al neutralizar
            emocionalmente los 7 minutos previos a tu salida y los 7 minutos
            posteriores a tu entrada, reprogramamos el sistema nervioso de tu
            perro. Pasamos de un estado de "alerta máxima" a un estado de
            "aburrimiento seguro", donde quedarse solo se convierte simplemente
            en el momento de la siesta.
          </p>
        </div>
      </section>

      {/* What You Get */}
      <section className="bg-cream-50 py-10 md:py-16" data-id="element-48">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-49">
          <h2 className="text-2xl md:text-4xl font-bold text-navy-900 mb-6 md:mb-10" data-id="element-50">
            Qué Incluye la Guía
          </h2>
          <div className="space-y-4 md:space-y-6" data-id="element-51">
            <div className="flex gap-4" data-id="element-52">
              <CheckIcon className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" data-id="element-53" />
              <div data-id="element-54">
                <p className="text-lg font-semibold text-navy-900" data-id="element-55">
                  Guía paso a paso: El protocolo exacto de Salida Invisible
                </p>
              </div>
            </div>
            <div className="flex gap-4" data-id="element-56">
              <CheckIcon className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" data-id="element-57" />
              <div data-id="element-58">
                <p className="text-lg font-semibold text-navy-900" data-id="element-59">
                  El Método de los 7 Minutos: Cómo entrar a casa para desactivar
                  la ansiedad de inmediato
                </p>
              </div>
            </div>
            <div className="flex gap-4" data-id="element-60">
              <CheckIcon className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" data-id="element-61" />
              <div data-id="element-62">
                <p className="text-lg font-semibold text-navy-900" data-id="element-63">
                  El "Buda Masticatorio": Cómo crear sustitutos de mandíbula
                  caseros que lo mantengan ocupado por 40 minutos
                </p>
              </div>
            </div>
            <div className="flex gap-4" data-id="element-64">
              <CheckIcon className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" data-id="element-65" />
              <div data-id="element-66">
                <p className="text-lg font-semibold text-navy-900" data-id="element-67">
                  Checklist de Casa a Prueba de Crisis: Ajustes en tu living que
                  bajan el estrés visual del perro
                </p>
              </div>
            </div>
            <div className="flex gap-4" data-id="element-68">
              <CheckIcon className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" data-id="element-69" />
              <div data-id="element-70">
                <p className="text-lg font-semibold text-navy-900" data-id="element-71">
                  Plan de Acción de 7 Días: El cronograma diario desde que te
                  despertás hasta que volvés
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who This Is For / Not For */}
      <section className="bg-white py-10 md:py-16" data-id="element-72">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-73">
          <h2 className="text-2xl md:text-4xl font-bold text-navy-900 mb-6 md:mb-10" data-id="element-74">
            ¿Es Para Vos?
          </h2>
          <ExpandableSection preview={<div className="space-y-8" data-id="element-76">
                <div data-id="element-77">
                  <h3 className="text-2xl font-bold text-green-700 mb-6" data-id="element-78">
                    ✓ Es para vos si...
                  </h3>
                  <div className="space-y-4" data-id="element-79">
                    <div className="flex gap-4" data-id="element-80">
                      <CheckIcon className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" data-id="element-81" />
                      <p className="text-lg text-gray-700" data-id="element-82">
                        Trabajás fuera de casa y vivís en departamentos o
                        espacios reducidos
                      </p>
                    </div>
                    <div className="flex gap-4" data-id="element-83">
                      <CheckIcon className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" data-id="element-84" />
                      <p className="text-lg text-gray-700" data-id="element-85">
                        Sufrís quejas de vecinos por aullidos o llantos
                        constantes
                      </p>
                    </div>
                  </div>
                </div>
              </div>} fullContent={<div className="space-y-8 mt-6" data-id="element-86">
                <div className="space-y-4" data-id="element-87">
                  <div className="flex gap-4" data-id="element-88">
                    <CheckIcon className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" data-id="element-89" />
                    <p className="text-lg text-gray-700" data-id="element-90">
                      Tenés un "perro sombra" que no puede quedarse solo ni para
                      que vayas al baño
                    </p>
                  </div>
                  <div className="flex gap-4" data-id="element-91">
                    <CheckIcon className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" data-id="element-92" />
                    <p className="text-lg text-gray-700" data-id="element-93">
                      Sos inquilino con miedo a perder el depósito por daños en
                      puertas o paredes
                    </p>
                  </div>
                </div>

                <div data-id="element-94">
                  <h3 className="text-2xl font-bold text-red-700 mb-6" data-id="element-95">
                    ✗ No es para vos si...
                  </h3>
                  <div className="space-y-4" data-id="element-96">
                    <div className="flex gap-4" data-id="element-97">
                      <XIcon className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" data-id="element-98" />
                      <p className="text-lg text-gray-700" data-id="element-99">
                        Buscás una solución mágica sin dedicar 10 minutos al día
                        al entrenamiento
                      </p>
                    </div>
                    <div className="flex gap-4" data-id="element-100">
                      <XIcon className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" data-id="element-101" />
                      <p className="text-lg text-gray-700" data-id="element-102">
                        Tu perro tiene cuadros de agresividad severa hacia
                        personas (requieren visita presencial)
                      </p>
                    </div>
                    <div className="flex gap-4" data-id="element-103">
                      <XIcon className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" data-id="element-104" />
                      <p className="text-lg text-gray-700" data-id="element-105">
                        Preferís seguir pagando fortunas en medicación antes que
                        cambiar tu propia rutina de salida
                      </p>
                    </div>
                  </div>
                </div>
              </div>} data-id="element-75" />
        </div>
      </section>

      {/* How It Works */}
      <section className="bg-cream-50 py-10 md:py-16" data-id="element-106">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-107">
          <h2 className="text-2xl md:text-4xl font-bold text-navy-900 mb-6 md:mb-10" data-id="element-108">
            Cómo Funciona
          </h2>
          <div className="space-y-6 md:space-y-8" data-id="element-109">
            <div className="flex gap-4 md:gap-6" data-id="element-110">
              <div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold text-lg md:text-xl" data-id="element-111">
                1
              </div>
              <div data-id="element-112">
                <p className="text-base md:text-lg text-gray-700 leading-relaxed" data-id="element-113">
                  Descargás la guía en PDF y realizás el diagnóstico de los
                  "disparadores" de tu perro.
                </p>
              </div>
            </div>
            <div className="flex gap-4 md:gap-6" data-id="element-114">
              <div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold text-lg md:text-xl" data-id="element-115">
                2
              </div>
              <div data-id="element-116">
                <p className="text-base md:text-lg text-gray-700 leading-relaxed" data-id="element-117">
                  Aplicás el protocolo de "Falsas Salidas" para quitarle poder a
                  tus llaves y zapatos.
                </p>
              </div>
            </div>
            <div className="flex gap-4 md:gap-6" data-id="element-118">
              <div className="flex-shrink-0 w-10 h-10 md:w-12 md:h-12 bg-orange-600 text-white rounded-full flex items-center justify-center font-bold text-lg md:text-xl" data-id="element-119">
                3
              </div>
              <div data-id="element-120">
                <p className="text-base md:text-lg text-gray-700 leading-relaxed" data-id="element-121">
                  Implementás la regla de los 7 minutos de silencio al entrar y
                  salir de tu hogar.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Proof */}
      <section className="bg-white py-10 md:py-16" data-id="element-122">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-123">
          <div className="bg-orange-50 border-l-4 border-orange-600 p-5 md:p-8 rounded-lg" data-id="element-124">
            <p className="text-lg md:text-xl text-gray-800 leading-relaxed font-medium" data-id="element-125">
              En solo 7 días, la mayoría de los dueños informan que sus perros
              dejan de jadear antes de la salida y comienzan a ignorar el sonido
              de la puerta, eligiendo quedarse en su cama en lugar de montar
              guardia.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-cream-50 py-10 md:py-16" data-id="element-126">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-127">
          <h2 className="text-2xl md:text-4xl font-bold text-navy-900 mb-6 md:mb-10" data-id="element-128">
            Preguntas Frecuentes
          </h2>
          <div className="bg-white rounded-lg shadow-md p-4 md:p-6" data-id="element-129">
            <FAQItem question="¿Tengo que dejar de acariciar a mi perro?" answer="No, solo aprendés CUÁNDO hacerlo para que no refuerce su miedo." data-id="element-130" />
            <FAQItem question="¿Sirve para perros adultos?" answer="Sí, incluso los perros senior pueden aprender que la soledad es segura." data-id="element-131" />
            <FAQItem question="¿Necesito comprar juguetes caros?" answer="No, la guía te enseña a usar lo que ya tenés en casa de forma estratégica." data-id="element-132" />
            <FAQItem question="¿Cuánto tiempo tarda en funcionar?" answer="Los cambios en la energía del perro se ven desde el primer día de aplicación." data-id="element-133" />
          </div>
        </div>
      </section>

      <BonusSection data-id="element-134" />
      <ValueStack data-id="element-135" />

      {/* Offer + Price Block */}
      <section id="cta-section" className="bg-white py-10 md:py-16" data-id="element-136">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-137">
          <div className="bg-gradient-to-br from-orange-50 to-cream-50 rounded-2xl shadow-2xl p-5 md:p-12 border-2 border-orange-200" data-id="element-138">
            <div className="grid md:grid-cols-2 gap-8 md:gap-10 items-center" data-id="element-139">
              <div data-id="element-140">
                <h2 className="text-2xl md:text-4xl font-bold text-navy-900 mb-4 md:mb-6" data-id="element-141">
                  Recuperá la paz de volver a casa y el amor por tu mascota sin
                  el estrés de los destrozos
                </h2>
                <p className="text-lg md:text-xl text-gray-700 mb-6 md:mb-8 leading-relaxed" data-id="element-142">
                  Accedé a la guía completa{' '}
                  <strong data-id="element-143">"Mascota Sola: Guía Anti-Destrucción"</strong> por un
                  pago único.
                </p>
                <div className="mb-6 md:mb-8" data-id="element-144">
                  <p className="text-4xl md:text-5xl font-bold text-orange-600 mb-2" data-id="element-145">
                    $8.499 ARS
                  </p>
                  <p className="text-gray-600 font-medium text-sm md:text-base" data-id="element-146">
                    Solo $8.499 ARS — Pago único — Acceso inmediato
                  </p>
                  <button onClick={handleAddToCart} className="mt-4 bg-orange-600 hover:bg-orange-700 text-white font-bold py-2.5 px-5 rounded-lg text-sm transition-all transform hover:scale-105 shadow-md md:hidden" data-id="element-147">
                    Quiero el método + bonos gratis
                  </button>
                </div>

                <div className="mb-6 bg-white rounded-xl border border-gray-200 shadow-sm p-4" data-id="element-148">
                  <div className="flex items-center gap-2 mb-2" data-id="element-149">
                    <ShieldCheckIcon className="w-4 h-4 text-green-600" data-id="element-150" />
                    <span className="text-sm font-bold text-navy-900" data-id="element-151">
                      Pack completo: Guía + 3 bonos
                    </span>
                  </div>
                  <div className="space-y-3" data-id="element-152">
                    <div className="flex items-center gap-3" data-id="element-153">
                      <img src="https://cdn.magicpatterns.com/uploads/gnbtUV32q9HPhUMH9igADJ/Mockup.png" alt="Guía principal" className="w-12 h-14 object-cover rounded-md shadow-sm flex-shrink-0" data-id="element-154" />
                      <div className="min-w-0" data-id="element-155">
                        <p className="text-xs font-bold text-navy-900" data-id="element-156">
                          Guía Anti-Destrucción
                        </p>
                        <p className="text-[11px] text-gray-500 leading-snug" data-id="element-157">
                          El método completo de los 7 minutos paso a paso.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3" data-id="element-158">
                      <img src="https://cdn.magicpatterns.com/uploads/rT4ezts7uuStAPPr8ZbeW2/Bono_1.png" alt="Bono 1" className="w-12 h-14 object-cover rounded-md shadow-sm flex-shrink-0" data-id="element-159" />
                      <div className="min-w-0 flex-1" data-id="element-160">
                        <p className="text-xs font-bold text-navy-900" data-id="element-161">
                          Kit Express de Salida Sin Drama
                        </p>
                        <p className="text-[11px] text-gray-500 leading-snug" data-id="element-162">
                          Secuencia exacta para los 10 min previos a salir.
                        </p>
                        {showBonus1 && <p className="text-[11px] text-gray-600 leading-snug mt-1" data-id="element-163">
                            Checklist imprimible + hoja de rescate para el
                            celular. Aprendé qué hacer, qué evitar y cómo cruzar
                            la puerta sin generar pánico.
                          </p>}
                        <button onClick={() => setShowBonus1(!showBonus1)} className="text-[11px] text-orange-600 font-medium hover:underline mt-0.5" data-id="element-164">
                          {showBonus1 ? 'Ocultar' : 'Ver más'}
                        </button>
                      </div>
                    </div>

                    <div className="flex items-start gap-3" data-id="element-165">
                      <img src="https://cdn.magicpatterns.com/uploads/xuW8s6CjcWcKjQ19gSGMDV/Bono_2.png" alt="Bono 2" className="w-12 h-14 object-cover rounded-md shadow-sm flex-shrink-0" data-id="element-166" />
                      <div className="min-w-0 flex-1" data-id="element-167">
                        <p className="text-xs font-bold text-navy-900" data-id="element-168">
                          Checklist Casa Calma
                        </p>
                        <p className="text-[11px] text-gray-500 leading-snug" data-id="element-169">
                          Detectá qué zonas de tu casa generan estrés.
                        </p>
                        {showBonus2 && <p className="text-[11px] text-gray-600 leading-snug mt-1" data-id="element-170">
                            Auditoría por zonas + hoja de detonantes + mapa de
                            entorno ideal. Identificá estímulos visuales y
                            sonoros que mantienen a tu perro en alerta.
                          </p>}
                        <button onClick={() => setShowBonus2(!showBonus2)} className="text-[11px] text-orange-600 font-medium hover:underline mt-0.5" data-id="element-171">
                          {showBonus2 ? 'Ocultar' : 'Ver más'}
                        </button>
                      </div>
                    </div>

                    <div className="flex items-start gap-3" data-id="element-172">
                      <img src="https://cdn.magicpatterns.com/uploads/to5JRkd7eYfdiSbo8eZhWv/Bono_3.png" alt="Bono 3" className="w-12 h-14 object-cover rounded-md shadow-sm flex-shrink-0" data-id="element-173" />
                      <div className="min-w-0 flex-1" data-id="element-174">
                        <p className="text-xs font-bold text-navy-900" data-id="element-175">
                          Recetario de Masticación Tranquila
                        </p>
                        <p className="text-[11px] text-gray-500 leading-snug" data-id="element-176">
                          5 opciones caseras para calmar a tu perro solo.
                        </p>
                        {showBonus3 && <p className="text-[11px] text-gray-600 leading-snug mt-1" data-id="element-177">
                            Recetas simples con ingredientes de casa + checklist
                            de seguridad + versión rápida para días con poco
                            tiempo. El momento exacto para entregarlo.
                          </p>}
                        <button onClick={() => setShowBonus3(!showBonus3)} className="text-[11px] text-orange-600 font-medium hover:underline mt-0.5" data-id="element-178">
                          {showBonus3 ? 'Ocultar' : 'Ver más'}
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-100" data-id="element-179">
                      <span className="text-sm text-gray-400 line-through" data-id="element-180">
                        $45.499
                      </span>
                      <span className="text-base font-bold text-orange-600" data-id="element-181">
                        $8.499 ARS
                      </span>
                      
                    </div>
                  </div>
                </div>

                <button onClick={handleAddToCart} className="w-full bg-orange-600 hover:bg-orange-700 text-white font-bold py-4 md:py-5 px-6 md:px-8 rounded-lg text-lg md:text-xl transition-all transform hover:scale-105 shadow-lg mb-3 md:mb-4" data-id="element-182">
                  Quiero el Método de los 7 Minutos + mis bonos
                </button>
                <p className="text-center text-red-600 font-medium text-xs md:text-sm mt-3 md:mt-4" data-id="element-183">
                  Los bonos están disponibles solo mientras dure esta oferta.
                  Una vez que cerrás esta página, no garantizamos que sigan
                  siendo gratis.
                </p>
              </div>
              <div className="flex justify-center" data-id="element-184">
                <img src="https://cdn.magicpatterns.com/uploads/gnbtUV32q9HPhUMH9igADJ/Mockup.png" alt="Mascota Sola - Guía Anti-Destrucción" className="w-full max-w-xs md:max-w-sm rounded-lg shadow-xl" data-id="element-185" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Sticky CTA */}

      <Footer data-id="element-186" />
    </div>;
}